import { create } from "zustand";
import { InventoryType } from "~/utils/constants";
import { TInventory } from "~/types/inventory";
import { Dispatch, SetStateAction } from "react";

type ControlMode = "inventory" | "bookings";

type InventoryControlFilters = {
  // Control mode and UI state
  controlMode: ControlMode;
  selectedDate: string;
  compactView: boolean;
  showFilters: boolean;
  searchTerm: string;

  // Filter state
  selectedVessel: string;
  selectedClient: string;
  selectedLocation: string;
  itemType: InventoryType | "all";
};

type State = {
  filters: InventoryControlFilters;

  // Selection state (not part of filters)
  selectedItems: string[];
  allSelectedInventories: TInventory[];
  
  // Track selection state across pagination
  selectionMode: 'all' | 'partial';
  excludedItems: string[]; // Used when selectionMode is 'all'
};

type Actions = {
  // Filter actions
  setFilters: (filters: Partial<InventoryControlFilters>) => void;
  resetFilters: () => void;

  // Selection actions
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  toggleSelection: (id: string) => void;
  setAllSelectedInventories: (inventories: TInventory[]) => void;
  
  // Advanced selection actions
  selectAll: (_allIds?: string[]) => void;
  deselectAll: () => void;
  toggleSelectionMode: () => void;
  toggleItemInSelectionMode: (id: string, _allIds?: string[]) => void;

  // Reset actions
  resetSelections: () => void;
  resetAll: () => void;
};

const initialFilters: InventoryControlFilters = {
  controlMode: "inventory",
  selectedDate: "",
  compactView: false,
  showFilters: false,
  searchTerm: "",
  selectedVessel: "all",
  selectedClient: "all",
  selectedLocation: "all",
  itemType: "all",
};

const initialState: State = {
  filters: initialFilters,
  selectedItems: [],
  allSelectedInventories: [],
  selectionMode: 'partial',
  excludedItems: [],
};

export const useInventoryControlStore = create<State & Actions>()((set) => ({
  ...initialState,

  // Filter actions
  setFilters: (filters) =>
    set((state) => ({
      filters: {
        ...state.filters,
        ...filters,
      },
    })),
  resetFilters: () =>
    set(() => ({
      filters: initialFilters,
    })),

  // Selection actions
  setSelectedItems: (items) =>
    set((state) => ({
      selectedItems:
        typeof items === "function" ? items(state.selectedItems) : items,
    })),
  toggleSelection: (id) =>
    set((state) => {
      // Handle toggle based on selection mode
      if (state.selectionMode === 'partial') {
        return {
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems.filter((item) => item !== id)
            : [...state.selectedItems, id],
        };
      } else {
        // In 'all' mode, we toggle by adding/removing from excludedItems
        return {
          excludedItems: state.excludedItems.includes(id)
            ? state.excludedItems.filter((item) => item !== id)
            : [...state.excludedItems, id],
        };
      }
    }),
  setAllSelectedInventories: (inventories) =>
    set({ allSelectedInventories: inventories }),
    
  // Advanced selection actions
  selectAll: (_allIds?) => 
    set({
      selectionMode: 'all',
      selectedItems: [],
      excludedItems: [],
    }),
  deselectAll: () =>
    set({
      selectionMode: 'partial',
      selectedItems: [],
      excludedItems: [],
    }),
  toggleSelectionMode: () =>
    set((state) => ({
      selectionMode: state.selectionMode === 'partial' ? 'all' : 'partial',
      selectedItems: [],
      excludedItems: [],
    })),
  toggleItemInSelectionMode: (id, _allIds?) =>
    set((state) => {
      if (state.selectionMode === 'all') {
        // In 'all' mode, we're toggling exclusions
        return {
          excludedItems: state.excludedItems.includes(id)
            ? state.excludedItems.filter(itemId => itemId !== id)
            : [...state.excludedItems, id]
        };
      } else {
        // In 'partial' mode, we're toggling inclusions
        return {
          selectedItems: state.selectedItems.includes(id)
            ? state.selectedItems.filter(itemId => itemId !== id)
            : [...state.selectedItems, id]
        };
      }
    }),

  // Reset actions
  resetSelections: () =>
    set({
      selectedItems: [],
      allSelectedInventories: [],
      excludedItems: [],
      selectionMode: 'partial',
      filters: {
        ...initialFilters,
        searchTerm: "",
      },
    }),
  resetAll: () => set(initialState),
}));
